import type { GnomeSpineStartupProperties } from "./gnomeSpineStartup.ts";

export interface AnalyticsCaptureClient {
    capture(event: string, properties?: Record<string, unknown>): unknown;
}

interface AnalyticsIdentityClient {
    identify(distinctId: string, properties?: Record<string, string>): unknown;
}

interface CommerceEventProperties {
    request_id: string;
    offer_id: string;
    offer_code: string;
    offer_revision_id: string;
    offer_version: string;
    price_id: string;
    price_xtr: string;
    surface:
        "CHAIN_OFFER" | "ENERGY_DRAWER" | "BEACH_COIN_OFFER" | "BUNDLE_OFFER";
    chain_journey_id?: string;
    experiment_decision_id?: string;
    experiment_key?: string;
    experiment_variant?: string;
}

interface ClientAnalyticsEventProperties {
    telegram_write_access_requested: { trigger: "first_completed_spin" };
    telegram_write_access_result: {
        trigger: "first_completed_spin";
        result: "allowed" | "cancelled" | "error";
    };
    app_initial_surface_ready: { ready_ms: number; surface: "home" };
    gnome_spine_preload_started: {
        attempt_id: string;
        preload_mode: "bytes" | "prepare";
        effective_mode: "bytes" | "prepare";
        preload_strategy: "startup_idle_v1";
        asset_pack_fingerprint: string;
        since_home_ready_ms: number;
    };
    gnome_spine_preload_finished: {
        attempt_id: string;
        preload_mode: "bytes" | "prepare";
        effective_mode: "bytes" | "prepare";
        preload_strategy: "startup_idle_v1";
        asset_pack_fingerprint: string;
        outcome: "ready" | "error" | "cancelled";
        phase: "core" | "outfit" | "prepare";
        metadata_outcome: "ready" | "absent" | "failed";
        duration_ms: number;
        pack_count: number;
        file_count: number;
        completed_file_count: number;
        body_bytes: number;
        queue_ms: number;
        fetch_body_ms: number;
    };
    gnome_spine_startup: GnomeSpineStartupProperties & {
        demand_id?: string;
        preload_mode?: "off" | "bytes" | "prepare";
        asset_prefetch_state_at_demand?: string;
        navigation_to_frame_ms?: number | null;
        bounds_source_fingerprint: string;
        asset_strategy: "equipped_packs_v1";
        asset_pack_fingerprint: string;
    };
    gnome_spine_demand_started: {
        demand_id: string;
        preload_mode?: "off" | "bytes" | "prepare";
        asset_prefetch_state_at_demand: string;
        initially_paused: boolean;
        asset_pack_fingerprint: string;
    };
    career_milestone_completed: {
        node_kind: "JOB";
        node_key: string;
        environment_key: null;
        job_run_id: string;
        job_id: string;
        job_rank: number;
        job_revision_id: string;
        path_revision_id: string;
        observed_at: string;
        response_replayed: boolean;
        event_source: "pbtg-client";
    };
    commerce_offer_viewed: {
        exposure_id: string;
        offer_code: string;
        offer_revision_id: string;
        price_xtr: string;
        surface: "BEACH_COIN_OFFER" | "BUNDLE_OFFER";
    };
    commerce_checkout_started: CommerceEventProperties;
    commerce_invoice_opened: CommerceEventProperties & {
        order_id: string;
    };
    commerce_invoice_closed: CommerceEventProperties & {
        order_id: string;
        invoice_status: "paid" | "cancelled" | "failed" | "pending";
    };
    commerce_checkout_failed: CommerceEventProperties & {
        order_id?: string;
        stage: "ORDER_CREATE" | "INVOICE_OPEN" | "SETTLEMENT" | "REFRESH";
        reason_code: string;
    };
    referral_share_initiated: {
        campaign_code: string;
        channel: "TELEGRAM_NATIVE" | "TELEGRAM_LINK";
    };
}

export const CLIENT_ANALYTICS_SCHEMA_VERSION = 1;
const PERSON_PROFILE_CACHE_PREFIX = "pbtg:posthog-person-profile:v1:";

export function captureClientAnalytics<
    EventName extends keyof ClientAnalyticsEventProperties,
>(
    client: AnalyticsCaptureClient,
    event: EventName,
    properties: ClientAnalyticsEventProperties[EventName],
): boolean {
    try {
        return (
            client.capture(event, {
                event_schema_version: CLIENT_ANALYTICS_SCHEMA_VERSION,
                ...properties,
            }) !== undefined
        );
    } catch {
        // Analytics must never interrupt an application workflow.
        return false;
    }
}

interface IdentifyPostHogPersonInput {
    userId: string;
    telegramUserId: number | null;
    locale: string;
    accountCreatedAt?: string | null;
}

/**
 * Identifies every browser session while avoiding a repeated `$set` when the
 * stable person properties have not changed. Local storage is only a volume
 * hint; PostHog identity and the server remain authoritative.
 */
export function identifyPostHogPerson(
    client: AnalyticsIdentityClient,
    storage: Pick<Storage, "getItem" | "setItem"> | null,
    input: IdentifyPostHogPersonInput,
): void {
    const properties: Record<string, string> = {
        locale: input.locale,
    };
    if (input.telegramUserId !== null) {
        properties.telegram_user_id = input.telegramUserId.toString();
    }

    if (input.accountCreatedAt) {
        properties.account_created_at = input.accountCreatedAt;
    }
    const signature = JSON.stringify(properties);
    const cacheKey = `${PERSON_PROFILE_CACHE_PREFIX}${input.userId}`;
    let shouldSetProperties = true;
    if (storage) {
        try {
            shouldSetProperties = storage.getItem(cacheKey) !== signature;
        } catch {
            // Storage failure only forfeits the volume optimization.
        }
    }

    try {
        client.identify(
            input.userId,
            shouldSetProperties ? properties : undefined,
        );
    } catch {
        return;
    }

    if (shouldSetProperties && storage) {
        try {
            storage.setItem(cacheKey, signature);
        } catch {
            // See the read-storage fallback above.
        }
    }
}
